import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'

export type InvestmentType = 'safe' | 'convertible_note' | 'priced_equity'

export type TermsData = {
  valuation_cap?: string
  discount?: string
  is_post_money?: boolean
  mfn?: boolean
  pro_rata_rights?: boolean
  interest_rate?: string
  maturity_date?: string
  seniority?: string
  shares_purchased?: string
  price_per_share?: string
  share_class?: string
  liquidation_preference_multiple?: string
  participation?: boolean
  anti_dilution_provision?: string
}

export type InvestmentFormData = {
  fund_id: number | ''
  company_id?: number | ''
  date: string
  amount_invested: number | ''
  investment_type: InvestmentType
  stage_at_investment: string
  terms: TermsData
}

export type Option = { id: number; name: string }

type Props = {
  data: InvestmentFormData
  errors: Record<string, string>
  setData: <K extends keyof InvestmentFormData>(key: K, value: InvestmentFormData[K]) => void
  funds: Option[]
  companies?: Option[]
  disabled?: boolean
}

function setTerms(
  setData: Props['setData'],
  current: TermsData,
  patch: Partial<TermsData>,
) {
  setData('terms', { ...current, ...patch })
}

export function InvestmentFormFields({
  data,
  errors,
  setData,
  funds,
  companies,
  disabled = false,
}: Props) {
  const err = (k: string) => errors[k]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Investment</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="fund_id">Fund</Label>
            <Select
              id="fund_id"
              value={data.fund_id === '' ? '' : String(data.fund_id)}
              onChange={(e) =>
                setData('fund_id', e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={disabled}
            >
              <option value="" disabled>
                Select a fund
              </option>
              {funds.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.name}
                </option>
              ))}
            </Select>
            {err('fund') && <p className="text-xs text-destructive">{err('fund')}</p>}
          </div>

          {companies && (
            <div className="space-y-1.5">
              <Label htmlFor="company_id">Company</Label>
              <Select
                id="company_id"
                value={data.company_id === '' || data.company_id == null ? '' : String(data.company_id)}
                onChange={(e) =>
                  setData('company_id', e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={disabled}
              >
                <option value="" disabled>
                  Select a company
                </option>
                {companies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </Select>
              {err('company') && <p className="text-xs text-destructive">{err('company')}</p>}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={data.date}
              onChange={(e) => setData('date', e.target.value)}
              disabled={disabled}
            />
            {err('date') && <p className="text-xs text-destructive">{err('date')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="amount_invested">Amount invested (USD)</Label>
            <Input
              id="amount_invested"
              type="number"
              value={data.amount_invested}
              onChange={(e) => {
                const v = e.target.value
                setData('amount_invested', v === '' ? '' : Number(v))
              }}
              disabled={disabled}
            />
            {err('amount_invested') && (
              <p className="text-xs text-destructive">{err('amount_invested')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="investment_type">Type</Label>
            <Select
              id="investment_type"
              value={data.investment_type}
              onChange={(e) => setData('investment_type', e.target.value as InvestmentType)}
              disabled={disabled}
            >
              <option value="safe">SAFE</option>
              <option value="convertible_note">Convertible note</option>
              <option value="priced_equity">Priced equity</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stage_at_investment">Stage</Label>
            <Input
              id="stage_at_investment"
              value={data.stage_at_investment}
              placeholder="Pre-Seed, Seed, Series A…"
              onChange={(e) => setData('stage_at_investment', e.target.value)}
              disabled={disabled}
            />
          </div>
        </CardContent>
      </Card>

      {data.investment_type === 'safe' && (
        <Card>
          <CardHeader>
            <CardTitle>SAFE terms</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="valuation_cap">Valuation cap (USD)</Label>
              <Input
                id="valuation_cap"
                type="number"
                value={data.terms.valuation_cap ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { valuation_cap: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discount">Discount (0–1)</Label>
              <Input
                id="discount"
                type="number"
                step="0.01"
                value={data.terms.discount ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { discount: e.target.value })}
                disabled={disabled}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.terms.is_post_money}
                onChange={(e) => setTerms(setData, data.terms, { is_post_money: e.target.checked })}
                disabled={disabled}
              />
              Post-money
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.terms.mfn}
                onChange={(e) => setTerms(setData, data.terms, { mfn: e.target.checked })}
                disabled={disabled}
              />
              MFN
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.terms.pro_rata_rights}
                onChange={(e) =>
                  setTerms(setData, data.terms, { pro_rata_rights: e.target.checked })
                }
                disabled={disabled}
              />
              Pro-rata rights
            </label>
          </CardContent>
        </Card>
      )}

      {data.investment_type === 'convertible_note' && (
        <Card>
          <CardHeader>
            <CardTitle>Convertible note terms</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="valuation_cap">Valuation cap (USD)</Label>
              <Input
                id="valuation_cap"
                type="number"
                value={data.terms.valuation_cap ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { valuation_cap: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discount">Discount (0–1)</Label>
              <Input
                id="discount"
                type="number"
                step="0.01"
                value={data.terms.discount ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { discount: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="interest_rate">Interest rate (0–1)</Label>
              <Input
                id="interest_rate"
                type="number"
                step="0.0001"
                value={data.terms.interest_rate ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { interest_rate: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maturity_date">Maturity date</Label>
              <Input
                id="maturity_date"
                type="date"
                value={data.terms.maturity_date ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { maturity_date: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seniority">Seniority</Label>
              <Input
                id="seniority"
                value={data.terms.seniority ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { seniority: e.target.value })}
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {data.investment_type === 'priced_equity' && (
        <Card>
          <CardHeader>
            <CardTitle>Priced equity terms</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="shares_purchased">Shares purchased</Label>
              <Input
                id="shares_purchased"
                type="number"
                value={data.terms.shares_purchased ?? ''}
                onChange={(e) =>
                  setTerms(setData, data.terms, { shares_purchased: e.target.value })
                }
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="price_per_share">Price per share (USD)</Label>
              <Input
                id="price_per_share"
                type="number"
                step="0.0001"
                value={data.terms.price_per_share ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { price_per_share: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="share_class">Share class</Label>
              <Input
                id="share_class"
                value={data.terms.share_class ?? ''}
                onChange={(e) => setTerms(setData, data.terms, { share_class: e.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="liquidation_preference_multiple">Liquidation pref.</Label>
              <Input
                id="liquidation_preference_multiple"
                type="number"
                step="0.01"
                value={data.terms.liquidation_preference_multiple ?? ''}
                onChange={(e) =>
                  setTerms(setData, data.terms, {
                    liquidation_preference_multiple: e.target.value,
                  })
                }
                disabled={disabled}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.terms.participation}
                onChange={(e) =>
                  setTerms(setData, data.terms, { participation: e.target.checked })
                }
                disabled={disabled}
              />
              Participation
            </label>
            <div className="space-y-1.5">
              <Label htmlFor="anti_dilution_provision">Anti-dilution</Label>
              <Input
                id="anti_dilution_provision"
                value={data.terms.anti_dilution_provision ?? ''}
                onChange={(e) =>
                  setTerms(setData, data.terms, { anti_dilution_provision: e.target.value })
                }
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
